import { useEffect, useState } from 'react';

import { supportsScreenCapture } from '@/utils/screen-capture-support';

/**
 * Whether this device can share a screen, resolved after hydration.
 *
 * `undefined` until the effect runs: the server cannot know, and rendering a
 * guess would either flash an unsupported warning at desktop users or hydrate
 * mismatched markup. Callers should gate on `=== false` so the "unknown" pass
 * leaves the UI alone.
 *
 * USE THIS ONLY FOR RENDER-TIME DECISIONS. `supportsScreenCapture()` reads
 * `navigator`, which is absent during SSR, so calling it inline while
 * rendering resolves to `false` on the server — every desktop participant
 * would be served "your device cannot record", then watch it disappear on
 * hydration. The effect is what buys the third state that avoids that.
 *
 * From an event handler, call `supportsScreenCapture()` directly instead (see
 * `use-screen-share.ts`): by then the browser is present, render timing is
 * irrelevant, and routing the answer through React state would only add a
 * frame of staleness.
 */
export function useScreenCaptureSupport(): boolean | undefined {
  const [supported, setSupported] = useState<boolean | undefined>();

  useEffect(() => {
    setSupported(supportsScreenCapture());
  }, []);

  return supported;
}
